import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getVideoById } from '@/lib/videoData';
import { getCategoryMeta } from '@/types/video';

interface Slide {
  id: string;
  backdrop: string;
  highlight: string;
}

const SLIDES: Slide[] = [
  {
    id: 't-001',
    backdrop:
      'https://readdy.ai/api/search-image?query=cinematic%20grand%20tang%20dynasty%20palace%20at%20night%20with%20hanging%20orange%20lanterns%20and%20misty%20courtyards%2C%20dramatic%20moody%20lighting%2C%20warm%20orange%20and%20teal%20color%20grading%2C%20atmospheric%2C%20no%20text%2C%20highly%20detailed&width=1920&height=1080&seq=peach-hero-01&orientation=landscape',
    highlight: '全网热播 · 古装悬疑巨制',
  },
  {
    id: 'm-001',
    backdrop:
      'https://readdy.ai/api/search-image?query=cinematic%20deep%20space%20nebula%20with%20a%20giant%20glowing%20orange%20planet%20and%20lone%20spacecraft%2C%20dramatic%20moody%20lighting%2C%20warm%20orange%20and%20teal%20color%20grading%2C%20epic%20atmospheric%2C%20no%20text%2C%20highly%20detailed&width=1920&height=1080&seq=peach-hero-02&orientation=landscape',
    highlight: '好莱坞科幻 · 独家上线',
  },
  {
    id: 'v-009',
    backdrop:
      'https://readdy.ai/api/search-image?query=cinematic%20victorian%20mansion%20hallway%20lit%20by%20candlelight%20with%20mysterious%20shadows%20and%20clues%2C%20dramatic%20moody%20lighting%2C%20warm%20orange%20and%20teal%20color%20grading%2C%20atmospheric%2C%20no%20text%2C%20highly%20detailed&width=1920&height=1080&seq=peach-hero-03&orientation=landscape',
    highlight: '人气推理综艺 · 每周更新',
  },
  {
    id: 't-009',
    backdrop:
      'https://readdy.ai/api/search-image?query=cinematic%20rainy%20neon%20city%20alley%20at%20night%20with%20two%20opposing%20silhouettes%20and%20warm%20orange%20reflections%2C%20dramatic%20moody%20lighting%2C%20warm%20orange%20and%20teal%20color%20grading%2C%20atmospheric%2C%20no%20text%2C%20highly%20detailed&width=1920&height=1080&seq=peach-hero-04&orientation=landscape',
    highlight: '港剧佳作 · 高能烧脑',
  },
  {
    id: 'v-001',
    backdrop:
      'https://readdy.ai/api/search-image?query=cinematic%20concert%20stage%20with%20warm%20orange%20spotlights%20and%20crowd%20silhouettes%2C%20hazy%20glowing%20atmosphere%2C%20dramatic%20moody%20lighting%2C%20warm%20orange%20and%20teal%20color%20grading%2C%20no%20text%2C%20highly%20detailed&width=1920&height=1080&seq=peach-hero-05&orientation=landscape',
    highlight: '音乐竞演 · 全场高能',
  },
];

export default function HeroCarousel() {
  const slides = SLIDES.map((s) => ({ ...s, video: getVideoById(s.id) })).filter(
    (s) => s.video
  );
  const [index, setIndex] = useState(0);
  const touchStart = useRef<number | null>(null);
  const count = slides.length;

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => setIndex((v) => (v + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (!count) return null;

  const go = (dir: number) => setIndex((v) => (v + dir + count) % count);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? 1 : -1);
    touchStart.current = null;
  };

  return (
    <section
      className="relative w-full overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="焦点推荐轮播"
    >
      <div className="relative h-[420px] w-full md:h-[560px]">
        {slides.map((slide, i) => {
          const meta = slide.video ? getCategoryMeta(slide.video.category) : undefined;
          return (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
              }`}
            >
              <img
                src={slide.backdrop}
                alt={`${slide.video?.title ?? ''}推荐背景`}
                title={`${slide.video?.title ?? ''} - 桃子视频`}
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/80 to-background-950/30" />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/40" />

              <div className="absolute inset-0 mx-auto flex max-w-7xl items-center px-4 md:px-8">
                <div className="flex w-full max-w-2xl flex-col items-start gap-4">
                  <span className="flex items-center gap-2 rounded-full bg-primary-500/20 px-3 py-1 text-xs font-medium text-primary-300">
                    <i className="ri-fire-fill" />
                    {slide.highlight}
                  </span>
                  <h2 className="font-heading text-3xl font-bold leading-tight text-foreground-50 md:text-5xl">
                    {slide.video?.title}
                  </h2>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-300">
                    <span className="rounded-md bg-background-800/80 px-2 py-0.5">{meta?.name}</span>
                    <span>{slide.video?.region}</span>
                    <span>{slide.video?.year}</span>
                    <span className="flex items-center gap-1 text-primary-300">
                      <i className="ri-star-fill text-xs" />
                      {slide.video?.rating.toFixed(1)}
                    </span>
                  </div>
                  <p className="max-w-xl text-sm leading-relaxed text-foreground-200 line-clamp-2 md:text-base md:line-clamp-3">
                    {slide.video?.description}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Link
                      to={`/detail/${slide.id}`}
                      className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-play-fill text-base" />
                      立即观看
                    </Link>
                    <Link
                      to={`/detail/${slide.id}`}
                      className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-600 px-5 py-2.5 text-sm font-medium text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-400"
                    >
                      <i className="ri-information-line text-base" />
                      查看详情
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一个"
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-600/70 bg-background-950/60 text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-400 md:flex"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一个"
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-600/70 bg-background-950/60 text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-400 md:flex"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到第${i + 1}张`}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? 'w-7 bg-primary-500' : 'w-2.5 bg-background-500 hover:bg-background-300'
            }`}
          />
        ))}
      </div>
    </section>
  );
}